import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Bug, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import ModalPortal from "../../components/ModalPortal";

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl border border-slate-200 shadow-sm ${className}`}>{children}</div>
);
const SEV = { minor:"bg-slate-100 text-slate-600", moderate:"bg-amber-100 text-amber-700",
  major:"bg-orange-100 text-orange-700", critical:"bg-rose-100 text-rose-700", emergency:"bg-rose-200 text-rose-800" };
const STATUSES = ["open","investigating","fix_ready","awaiting_approval","resolved","closed"];

function NewBugModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ title:"", severity:"moderate", affected:"", logs:"", source:"manual" });
  const [busy, setBusy] = useState(false);
  const inp = "w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm";
  const submit = async () => {
    if (form.title.trim().length < 3) { toast.error("Title required"); return; }
    setBusy(true);
    try { await api.post("/admin/ops/bugs", form); toast.success("Bug logged"); onCreated(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
        <div className="w-full max-w-md bg-white rounded-2xl p-6" onClick={(e)=>e.stopPropagation()} data-testid="opsbug-modal">
          <div className="flex justify-between items-center"><h3 className="font-bold text-slate-900">Report a bug</h3><button onClick={onClose}><X size={18} className="text-slate-400"/></button></div>
          <div className="mt-4 space-y-3">
            <label className="block text-xs text-slate-500">Title<input value={form.title} onChange={(e)=>setForm(f=>({...f,title:e.target.value}))} className={inp} data-testid="opsbug-title"/></label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs text-slate-500">Severity
                <select value={form.severity} onChange={(e)=>setForm(f=>({...f,severity:e.target.value}))} className={inp} data-testid="opsbug-severity">
                  {Object.keys(SEV).map(s=><option key={s} value={s}>{s}</option>)}
                </select></label>
              <label className="block text-xs text-slate-500">Affected page/API<input value={form.affected} onChange={(e)=>setForm(f=>({...f,affected:e.target.value}))} className={inp} data-testid="opsbug-affected"/></label>
            </div>
            <label className="block text-xs text-slate-500">Logs / error details
              <textarea rows={4} value={form.logs} onChange={(e)=>setForm(f=>({...f,logs:e.target.value}))} className={inp} data-testid="opsbug-logs"/></label>
          </div>
          <button onClick={submit} disabled={busy} className="mt-4 w-full py-2.5 rounded-xl bg-rose-600 text-white font-bold text-sm" data-testid="opsbug-submit">
            {busy ? "Logging…" : "Log bug"}</button>
        </div>
      </div>
    </ModalPortal>
  );
}

export default function AdminOpsBugs() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [diagBusy, setDiagBusy] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const load = useCallback(() => { api.get("/admin/ops/bugs").then(r=>setRows(r.data.rows)).catch(()=>toast.error("Failed")); }, []);
  useEffect(load, [load]);
  const diagnose = async (id) => {
    setDiagBusy(id);
    try { await api.post(`/admin/ops/bugs/${id}/diagnose`); toast.success("AI diagnosis ready"); setExpanded(id); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Diagnosis failed"); }
    finally { setDiagBusy(null); }
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/ops/bugs/${id}`, { status }); load(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div data-testid="aiops-bugs">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-500">Fixes are prepared in the preview environment — production deployment always needs your approval & redeploy.</p>
        <button onClick={()=>setOpen(true)} className="px-3 py-2 rounded-lg bg-rose-600 text-white text-xs font-bold inline-flex items-center gap-1.5" data-testid="opsbug-new"><Plus size={13}/> Report bug</button>
      </div>
      {!rows ? <Loader2 className="animate-spin mx-auto my-10 text-emerald-600"/> :
        rows.length === 0 ? <Card className="p-8 text-center text-sm text-slate-400"><Bug size={24} className="mx-auto mb-2 text-slate-300"/>No bugs logged. 🎉</Card> : (
        <div className="space-y-2.5">
          {rows.map(b => (
            <Card key={b.id} className="p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[11px] text-rose-700">{b.number}</span>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${SEV[b.severity]}`}>{b.severity}</span>
                    <span className="text-[10px] text-slate-400">{b.source} · {b.affected || "unspecified"}</span>
                  </div>
                  <div className="font-bold text-slate-900 mt-1">{b.title}</div>
                </div>
                <div className="flex gap-2 items-center shrink-0">
                  <select value={b.status} onChange={(e)=>setStatus(b.id, e.target.value)} className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs font-bold" data-testid={`opsbug-status-${b.number}`}>
                    {STATUSES.map(s=><option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
                  </select>
                  <button onClick={()=>diagnose(b.id)} disabled={diagBusy===b.id}
                    className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1" data-testid={`opsbug-diagnose-${b.number}`}>
                    {diagBusy===b.id ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>} AI diagnose
                  </button>
                </div>
              </div>
              {b.diagnosis && (
                <div className="mt-3">
                  <button onClick={()=>setExpanded(expanded===b.id?null:b.id)} className="text-xs font-bold text-emerald-700" data-testid={`opsbug-toggle-diag-${b.number}`}>
                    {expanded===b.id ? "Hide" : "Show"} AI diagnosis
                  </button>
                  {expanded===b.id && (
                    <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-xs text-slate-700 whitespace-pre-wrap" data-testid={`opsbug-diag-${b.number}`}>{b.diagnosis}</div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
      {open && <NewBugModal onClose={()=>setOpen(false)} onCreated={()=>{ setOpen(false); load(); }}/>}
    </div>
  );
}
